import { createButtonTokens, createToolbarTokens } from '../tokens/components.js';
import { type SemanticTokens, SEMANTIC } from '../tokens/semantic.js';

/**
 * Toolbar Component Configuration
 *
 * Full-width toolbar with top/bottom borders, no border-radius.
 * Buttons inside the toolbar are plain <button> tags with custom classes:
 * - `.p-toolbar-primary`: magenta filled button (main CTA)
 * - `.p-toolbar-secondary`: white ghost button with dark text; hover turns pink + magenta text
 */
export function createToolbar(s: SemanticTokens) {
  const buttonTokens = createButtonTokens(s);
  const toolbarTokens = createToolbarTokens(s);
  return {
  root: {
    background: toolbarTokens.bg,
    borderRadius: '0',
    color: toolbarTokens.fg,
    gap: '0',
    padding: '0',
  },
  css: () => `
    .p-toolbar {
      background: ${toolbarTokens.bg};
      border-bottom: ${toolbarTokens.base.borderWidth} solid ${toolbarTokens.border};
      border-top: ${toolbarTokens.base.borderWidth} solid ${toolbarTokens.border};
      border-left: 0;
      border-right: 0;
      padding: 0 ${toolbarTokens.base.paddingX};
    }

    .p-toolbar-start,
    .p-toolbar-center,
    .p-toolbar-end {
      display: flex;
      align-items: center;
    }

    .p-toolbar-start {
      gap: ${toolbarTokens.start.gap};
      flex: 1;
    }

    /* Shared base for all toolbar buttons */
    .p-toolbar-primary,
    .p-toolbar-secondary {
      display: inline-flex;
      align-items: center;
      gap: ${toolbarTokens.button.gap};
      padding: ${toolbarTokens.button.padding};
      height: 100%;
      border-top: 0;
      border-bottom: 0;
      border-left: ${toolbarTokens.base.borderWidth} solid ${toolbarTokens.border};
      border-right: ${toolbarTokens.base.borderWidth} solid ${toolbarTokens.border};
      border-radius: 0;
      font-family: ${toolbarTokens.button.fontFamily};
      font-size: ${toolbarTokens.button.fontSize};
      font-weight: ${toolbarTokens.button.fontWeight};
      line-height: ${toolbarTokens.button.lineHeight};
      cursor: pointer;
      outline: none;
      box-shadow: none;
      transition: background ${toolbarTokens.button.transition} ease-in-out, color ${toolbarTokens.button.transition} ease-in-out;
    }

    .p-toolbar-primary .pi,
    .p-toolbar-secondary .pi {
      font-size: ${toolbarTokens.button.iconSize};
    }

    .p-toolbar-primary + .p-toolbar-primary,
    .p-toolbar-secondary + .p-toolbar-secondary,
    .p-toolbar-primary + .p-toolbar-secondary,
    .p-toolbar-secondary + .p-toolbar-primary {
      border-left: 0;
    }

    .p-toolbar-primary:focus,
    .p-toolbar-secondary:focus {
      outline: 0;
      box-shadow: none;
    }

    /* Primary button â€” magenta filled */
    .p-toolbar-primary {
      background: ${buttonTokens.primary.bg};
      color: ${buttonTokens.primary.fg};
      border-color: ${buttonTokens.primary.bg};
    }

    .p-toolbar-primary:hover {
      background: ${buttonTokens.primary.bgHover};
      border-color: ${buttonTokens.primary.bgHover};
      color: ${buttonTokens.primary.fg};
    }

    .p-toolbar-primary:active {
      background: ${buttonTokens.primary.bgPressed};
      border-color: ${buttonTokens.primary.bgPressed};
      color: ${buttonTokens.primary.fg};
    }

    /* Secondary button â€” ghost/action style */
    .p-toolbar-secondary {
      background: ${buttonTokens.action.bg};
      color: ${buttonTokens.action.fg};
    }

    .p-toolbar-secondary:hover {
      background: ${buttonTokens.action.bgHover};
      color: ${buttonTokens.action.fgHover};
    }

    .p-toolbar-secondary:active {
      background: ${buttonTokens.primary.bgPressed};
      color: ${buttonTokens.primary.fg};
    }

    /* Disabled state */
    .p-toolbar-primary:disabled,
    .p-toolbar-secondary:disabled {
      cursor: not-allowed;
      pointer-events: none;
    }

    .p-toolbar-primary:disabled {
      background: ${buttonTokens.primary.bgDisabled};
      border-color: ${buttonTokens.primary.bgDisabled};
      color: ${buttonTokens.primary.fgDisabled};
    }

    .p-toolbar-secondary:disabled {
      background: ${buttonTokens.action.bgDisabled};
      color: ${buttonTokens.action.fgDisabled};
    }
  `,
  };
}
export const toolbar = createToolbar(SEMANTIC);